import React from 'react';
import styles from '../insertsPages.module.css';
import { SelectionOption } from '../../../SelectionOption';
import { usePage } from '../../../../context/PageContext';
import { getPageOptions, pages, resolveClicksToTargets } from '../../../../context/pages';
import { OptionCard } from '../../../../packaging/components/OptionCard';
import { NameField } from '../../../../packaging/components/NameField';
import packagingStyles from '../../../../packaging/packaging.module.css';
import { useGiftWrapContext } from '../../../../context/GiftWrapContext';
import { GiftPresentation } from '../../../GiftPresentation';

type OptionSelectionOption = {
  icon?: string;
  title?: string;
  subtitle?: string;
  getSubtitle?: () => string;
  clicksTo?: unknown;
  disabled?: boolean;
  hero?: boolean;
  imageUrl?: string;
};

type PriceRange = { minPounds: number; maxPounds: number };

/** A message field drawn on one packaging card (step 3's Merry Christmas card). */
type MessageCard = {
  optionIndex: number;
  maxLength: number;
  value: string;
  onChange: (value: string) => void;
  /** What the field keeps of typed text. */
  clean: (value: string) => string;
};

type OptionSelectionPageProps = {
  pageName: string;
  headerTitle?: string;
  subtitleOverrides?: Record<number, string>;
  hiddenOptionIndexes?: Set<number>;
  priceRanges?: Record<number, PriceRange>;
  messageCard?: MessageCard | null;
};

/**
 * Generic OptionSelectionPage component that renders selection options based on page configuration.
 * This replaces individual page components like AddPersonalTouch, ChooseInsertPersonalisation, etc.
 */
export function OptionSelectionPage({
  pageName,
  headerTitle,
  subtitleOverrides,
  hiddenOptionIndexes,
  priceRanges,
  messageCard = null
}: OptionSelectionPageProps) {
  const { getPageSelection, setPageSelection, goToPage } = usePage();
  const { giftInfo, setGiftInfo } = useGiftWrapContext();
  const selectedOptionIndex = getPageSelection(pageName);

  const pageConfig = (pages as Record<string, { autoAdvance?: boolean; cardStyle?: string }>)[pageName];
  const autoAdvance = pageConfig?.autoAdvance !== false;

  const handleSelectOption = ({
    index,
    option
  }: {
    index: number;
    option: OptionSelectionOption;
  }) => {
    // Radio-style page (step 3): select or deselect, and let the footer CONTINUE navigate.
    if (!autoAdvance) {
      setPageSelection(pageName, selectedOptionIndex === index ? null : index);
      return;
    }

    setPageSelection(pageName, index);

    // Navigate to the option destination, matching the config-driven footer behavior.
    const [destination] = resolveClicksToTargets(option?.clicksTo);
    if (typeof destination !== 'string' || destination.length === 0) {
      return;
    }

    setTimeout(() => {
      goToPage(destination);
    }, 300);
  };

  // Get options from centralized configuration.
  const options = getPageOptions(pageName) as OptionSelectionOption[];

  if (pageConfig?.cardStyle === 'packaging') {
    const messageField = (index: number) =>
      messageCard?.optionIndex === index ? (
        <NameField
          id="packaging-card-message"
          label="Write your message (Optional)"
          value={messageCard.value}
          maxLength={messageCard.maxLength}
          placeholder="Enter Message"
          clean={messageCard.clean}
          onChange={(text: string) => {
            messageCard.onChange(text);
            // Writing a message chooses the card.
            if (selectedOptionIndex !== index) setPageSelection(pageName, index);
          }}
        />
      ) : null;
    return (
      <>
        <div role="radiogroup" aria-label="One final touch" className={packagingStyles.cards}>
          {options.map((option, index) => {
            if (hiddenOptionIndexes?.has(index)) return null;
            // The lowest active price, with no "From" even when prices vary (ALBAU-894, client decision).
            // The main card's price: the highest in the path, as the option subtitles show (ALBAU-894).
            const price = priceRanges?.[index]?.maxPounds ?? 0;
            return (
              <OptionCard
                key={index}
                name={option.title}
                description={option.subtitle}
                price={price}
                heroImages={option.hero && option.imageUrl ? [option.imageUrl] : []}
                heroInset={Boolean(option.hero)}
                thumbUrl={option.hero ? '' : option.imageUrl ?? ''}
                selected={selectedOptionIndex === index}
                onSelect={() => handleSelectOption({ index, option })}
                field={messageField(index)}
                onInfo={() =>
                  setGiftInfo({
                    title: option.title,
                    price,
                    imageUrl: option.imageUrl ?? '',
                    description: option.subtitle
                  })
                }
              />
            );
          })}
        </div>
        {giftInfo && <GiftPresentation />}
      </>
    );
  }

  return (
    <div className={styles['inserts-pages--page']}>
      <div className={styles['page--container']}>
        {headerTitle && (
          <div className={styles['inserts-pages--header']}>
            <h1 className={styles['inserts-pages--title']}>{headerTitle}</h1>
          </div>
        )}
        <div className={styles['inserts-pages--options']}>
          {options.map((option, index) => {
            if (hiddenOptionIndexes?.has(index)) {
              return null;
            }

            // MVVM override takes precedence; fall back to static subtitle or legacy getSubtitle().
            const subtitle =
              subtitleOverrides?.[index] ??
              (typeof option.getSubtitle === 'function' ? option.getSubtitle() : option.subtitle);

            return (
              <SelectionOption
                key={index}
                icon={option.icon}
                title={option.title}
                subtitle={subtitle}
                isSelected={selectedOptionIndex === index}
                onClick={() => handleSelectOption({ index, option })}
                clicksTo={option.clicksTo}
                disabled={option.disabled}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
